import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const REPORT_REASONS = [
  { id: 'fake', label: 'Fake profile' },
  { id: 'harassment', label: 'Harassment' },
  { id: 'spam', label: 'Spam' },
  { id: 'inappropriate', label: 'Inappropriate content' },
  { id: 'underage', label: 'Underage' },
  { id: 'other', label: 'Other' },
];

// =====================
// BLOCK MODAL
// =====================
export function BlockModal({ user, myId, onConfirm, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleBlock = async () => {
    if (!myId || !user?.id) { setError('Session error. Please refresh.'); return; }
    setLoading(true);
    setError('');
    try {
      // Supprimer si déjà bloqué puis réinsérer
      await supabase.from('blocks').delete().eq('blocker_id', myId).eq('blocked_id', user.id);
      const { error: err } = await supabase.from('blocks').insert({ blocker_id: myId, blocked_id: user.id });
      if (err) throw err;
      setDone(true);
      setTimeout(() => { onConfirm(user.id); }, 1200);
    } catch (e) {
      console.error('Block error:', e);
      setError('Failed to block. Try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:300, padding:'0 0 24px' }} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'24px', padding:'28px 24px', width:'calc(100% - 32px)', maxWidth:'400px' }}>
        {!done ? (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'20px' }}>
              <div style={{ width:'48px', height:'48px', borderRadius:'50%', background:'rgba(200,30,30,0.12)', border:'1px solid rgba(200,30,30,0.25)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="9.5" stroke="#ff4444" strokeWidth="1.5"/><line x1="4" y1="4" x2="18" y2="18" stroke="#ff4444" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </div>
              <div>
                <div style={{ fontWeight:700, color:'white', fontSize:'1rem' }}>Block {user?.name||'this user'}?</div>
                <div style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.78rem', marginTop:'2px' }}>Reversible from your Profile page</div>
              </div>
            </div>

            <div style={{ background:'rgba(255,50,50,0.05)', border:'1px solid rgba(255,50,50,0.1)', borderRadius:'14px', padding:'14px', marginBottom:'20px' }}>
              {['They disappear from your map','They cannot message you','You won\'t appear on their map','All interactions cut immediately'].map((item, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'9px', marginBottom: i < 3 ? '9px' : 0 }}>
                  <div style={{ width:'5px', height:'5px', borderRadius:'50%', background:'rgba(255,100,100,0.6)', flexShrink:0 }}/>
                  <span style={{ color:'rgba(255,255,255,0.45)', fontSize:'0.8rem' }}>{item}</span>
                </div>
              ))}
            </div>

            {error && <div style={{ color:'#ff6b6b', fontSize:'0.8rem', marginBottom:'12px', textAlign:'center' }}>{error}</div>}

            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={onCancel} disabled={loading} style={{ flex:1, padding:'13px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'13px', color:'rgba(255,255,255,0.5)', fontSize:'0.9rem', cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={handleBlock} disabled={loading} style={{ flex:1, padding:'13px', background: loading ? 'rgba(200,30,30,0.5)' : '#cc2222', border:'none', borderRadius:'13px', color:'white', fontSize:'0.9rem', fontWeight:700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Blocking...' : 'Block'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign:'center', padding:'16px 0' }}>
            <div style={{ width:'52px', height:'52px', background:'rgba(200,30,30,0.12)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 12L9 16L19 7" stroke="#ff4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div style={{ color:'white', fontWeight:700, fontSize:'1rem', marginBottom:'4px' }}>User blocked</div>
            <div style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.82rem' }}>{user?.name} can no longer interact with you.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================
// REPORT MODAL
// =====================
export function ReportModal({ user, myId, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleReport = async () => {
    if (!reason) return;
    if (!myId || !user?.id) { setError('Session error. Please refresh.'); return; }
    setLoading(true);
    setError('');
    try {
      const { error: err } = await supabase.from('reports').insert({
        reporter_id: myId,
        reported_id: user.id,
        reason,
        message: message || null,
      });
      if (err) throw err;
      setDone(true);
      setTimeout(() => { onConfirm(); }, 1800);
    } catch (e) {
      console.error('Report error:', e);
      setError('Failed to submit. Try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:300, padding:'0 0 24px' }} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ background:'#111', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'24px', padding:'24px', width:'calc(100% - 32px)', maxWidth:'400px' }}>
        {!done ? (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'18px' }}>
              <div>
                <div style={{ fontWeight:700, color:'white', fontSize:'1rem' }}>Report {user?.name||'this user'}</div>
                <div style={{ color:'rgba(255,255,255,0.25)', fontSize:'0.75rem', marginTop:'2px' }}>Reviewed by our team within 24h</div>
              </div>
              <button onClick={onCancel} style={{ background:'rgba(255,255,255,0.07)', border:'none', borderRadius:'50%', width:'30px', height:'30px', cursor:'pointer', color:'rgba(255,255,255,0.4)', fontSize:'18px', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'7px', marginBottom:'14px' }}>
              {REPORT_REASONS.map(r => (
                <button key={r.id} onClick={() => setReason(r.label)} style={{ padding:'11px 12px', background: reason===r.label ? 'rgba(255,165,0,0.12)' : 'rgba(255,255,255,0.03)', border: `1px solid ${reason===r.label ? 'rgba(255,165,0,0.4)' : 'rgba(255,255,255,0.07)'}`, borderRadius:'12px', color: reason===r.label ? 'rgba(255,165,0,0.9)' : 'rgba(255,255,255,0.5)', fontSize:'0.82rem', cursor:'pointer', textAlign:'left', fontWeight: reason===r.label ? 600 : 400 }}>
                  {r.label}
                </button>
              ))}
            </div>

            <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Additional details (optional)..." rows={2}
              style={{ width:'100%', padding:'11px 13px', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', color:'white', fontSize:'0.82rem', resize:'none', outline:'none', marginBottom:'14px', boxSizing:'border-box' }}/>

            {error && <div style={{ color:'#ff6b6b', fontSize:'0.8rem', marginBottom:'10px', textAlign:'center' }}>{error}</div>}

            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={onCancel} style={{ flex:1, padding:'13px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'13px', color:'rgba(255,255,255,0.5)', fontSize:'0.9rem', cursor:'pointer' }}>Cancel</button>
              <button onClick={handleReport} disabled={!reason || loading} style={{ flex:1, padding:'13px', background: reason ? 'rgba(255,165,0,0.85)' : 'rgba(255,255,255,0.08)', border:'none', borderRadius:'13px', color: reason ? 'white' : 'rgba(255,255,255,0.2)', fontSize:'0.9rem', fontWeight:700, cursor: reason ? 'pointer' : 'not-allowed' }}>
                {loading ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign:'center', padding:'16px 0' }}>
            <div style={{ width:'52px', height:'52px', background:'rgba(0,214,143,0.1)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 12L9 16L19 7" stroke="#00d68f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div style={{ color:'white', fontWeight:700, fontSize:'1rem', marginBottom:'4px' }}>Report submitted</div>
            <div style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.82rem' }}>Our team will review within 24 hours.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================
// BLOCKED LIST — pour le profil
// =====================
export function BlockedList({ session }) {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);
  const myId = session?.user?.id;

  useEffect(() => {
    if (!myId) return;
    const load = async () => {
      const { data } = await supabase.from('blocks').select('blocked_id, created_at').eq('blocker_id', myId);
      if (!data?.length) { setLoading(false); return; }
      const { data: profiles } = await supabase.from('profiles').select('id, name, age, gender').in('id', data.map(b => b.blocked_id));
      setBlocked(data.map(b => ({ ...b, profile: profiles?.find(p => p.id === b.blocked_id) })));
      setLoading(false);
    };
    load();
  }, [myId]);

  const unblock = async (blockedId) => {
    setUnblocking(blockedId);
    await supabase.from('blocks').delete().eq('blocker_id', myId).eq('blocked_id', blockedId);
    setBlocked(prev => prev.filter(b => b.blocked_id !== blockedId));
    setUnblocking(null);
  };

  if (loading) return null;

  return (
    <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'16px', padding:'18px', marginBottom:'10px' }}>
      <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.25)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'12px' }}>
        Blocked users {blocked.length > 0 ? `(${blocked.length})` : ''}
      </div>
      {blocked.length === 0 ? (
        <div style={{ color:'rgba(255,255,255,0.18)', fontSize:'0.82rem', textAlign:'center', padding:'8px 0' }}>No blocked users.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {blocked.map(b => (
            <div key={b.blocked_id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'12px' }}>
              <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'rgba(255,50,50,0.08)', border:'1px solid rgba(255,50,50,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:700, color:'rgba(255,100,100,0.6)', flexShrink:0 }}>
                {(b.profile?.name||'?')[0].toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.85rem', fontWeight:500 }}>{b.profile?.name||'Unknown'}{b.profile?.age?`, ${b.profile.age}`:''}</div>
                <div style={{ color:'rgba(255,255,255,0.2)', fontSize:'0.72rem' }}>Blocked {new Date(b.created_at).toLocaleDateString('en',{day:'2-digit',month:'short'})}</div>
              </div>
              <button onClick={() => unblock(b.blocked_id)} disabled={unblocking === b.blocked_id} style={{ padding:'6px 14px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', color:'rgba(255,255,255,0.4)', fontSize:'0.78rem', cursor:'pointer' }}>
                {unblocking === b.blocked_id ? '...' : 'Unblock'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
